import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  Eye,
  PencilRuler,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { useModal } from "../../hooks/useModal";
import { ApiService } from "../../services/api";
import { getMaarifWizardSessionForRoute } from "../../services/maarifQuestionWizardSession";
import type { MaarifQuestionRecord } from "../../types/maarif-question-wizard";
import checkPermission from "../../utils/permission";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getRecordQuestionCount = (item: MaarifQuestionRecord) =>
  Array.isArray(item.content?.multiQuestions) && item.content.multiQuestions.length
    ? item.content.multiQuestions.length
    : item.content?.rewriteQuestion || item.content?.question
      ? 1
      : 0;

export default function MaarifQuestionWizardListPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm, showToast } = useModal();
  const canList = checkPermission("maarif_question_list");
  const canCreate = checkPermission("maarif_question_add");
  const canDelete = checkPermission("maarif_question_delete");
  const canOpenInQuestionBank = checkPermission("question_bank_list");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const pendingNewSession = getMaarifWizardSessionForRoute(null);
    if (!pendingNewSession) return;

    navigate("/maarif-question-wizard/new", { replace: true });
  }, [navigate]);

  const handleDelete = async (item: MaarifQuestionRecord) => {
    const confirmed = await showConfirm({
      title: "Kaydı Sil",
      message: `"${item.title}" kaydını silmek istediğinize emin misiniz?`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      await ApiService.deleteMaarifQuestionRecord(item.id);
      setRefreshKey((prev) => prev + 1);
      showToast("Kayıt silindi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Silme Hatası",
        message: error?.message || "Kayıt silinemedi.",
        type: "error",
      });
    }
  };

  const renderItem = (item: MaarifQuestionRecord) => {
    const questionCount = getRecordQuestionCount(item);
    const questionBankId = item.content?.questionBankExport?.questionBankId || null;

    return (
      <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">{item.title}</h3>
              <div className="text-xs text-gray-500">
                {item.workflowSource === "audited_upload" ? "Denetimden yeniden üretilen kayıt" : "Sihirbaz kaydı"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
            <div className="inline-flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-gray-400" />
              {item.lessonTitle || "-"}
            </div>
            <div className="inline-flex items-center gap-2">
              <PencilRuler className="w-4 h-4 text-gray-400" />
              {item.gradeLabel || "-"}
            </div>
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gray-400" />
              {item.difficulty}
            </div>
            <div className="inline-flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-gray-400" />
              {item.processComponentCode ? `${item.processComponentCode} • ${item.processComponentLabel || "-"}` : "-"}
            </div>
            <div className="inline-flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-gray-400" />
              Güncellendi: {formatDate(item.updatedAt)}
            </div>
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gray-400" />
              {questionCount > 1 ? `${questionCount} soru` : "Tek soru"}
            </div>
          </div>

          <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {item.gainText || "-"}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
              item.workflowSource === "audited_upload"
                ? "bg-amber-50 text-amber-700 border-amber-200"
                : "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}
          >
            {questionCount > 1
              ? "PDF + Çoklu Soru"
              : item.workflowSource === "audited_upload"
                ? "Denetim + Yeniden Yazım"
                : "Bağlam + Soru"}
          </span>
          <button
            onClick={() => navigate(`/maarif-question-wizard/${item.id}`)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
          >
            Aç
          </button>
          {questionBankId && canOpenInQuestionBank ? (
            <button
              onClick={() => navigate(`/question-bank-indesign/${questionBankId}/edit`)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 text-sm"
            >
              <Eye className="w-4 h-4" />
              Dizgicide Aç
            </button>
          ) : null}
          {canDelete ? (
            <button
              onClick={() => handleDelete(item)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </button>
          ) : null}
        </div>
      </div>
      </div>
    );
  };

  const actions = useMemo(
    () =>
      canCreate ? (
        <button
          onClick={() => navigate("/maarif-question-wizard/new")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Yeni Kayıt
        </button>
      ) : null,
    [canCreate, navigate]
  );

  const tabs = useMemo<TabItemProps[]>(
    () => [
      {
        type: "list",
        title: "Tüm Kayıtlar",
        path: "maarif-question-wizard",
        tabId: "all-maarif-question-wizard",
        isSearch: true,
        isPagination: true,
        refreshKey,
        actions,
        emptyMessage: "Henüz kaydedilmiş bir Maarif Soru Sihirbazı kaydı bulunmuyor.",
        fetcher: async () => {
          const response = await ApiService.listMaarifQuestionRecords();
          return {
            data: response.rows || [],
          };
        },
        dataProcessor: (rows, searchTerm) => {
          const term = String(searchTerm || "").trim().toLocaleLowerCase("tr-TR");
          const filtered = term
            ? rows.filter((item) =>
                [item.title, item.lessonTitle, item.gradeLabel, item.difficulty, item.gainText, item.processComponentCode, item.processComponentLabel]
                  .filter(Boolean)
                  .some((value) => String(value).toLocaleLowerCase("tr-TR").includes(term))
              )
            : rows;

          return [...filtered].sort((a, b) => {
            const aTime = new Date(a.updatedAt).getTime();
            const bTime = new Date(b.updatedAt).getTime();
            return bTime - aTime;
          });
        },
        clientPageSize: 12,
        item: (data) => renderItem(data as MaarifQuestionRecord),
      },
    ],
    [actions, refreshKey]
  );

  if (!canList) {
    return (
      <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="text-lg font-semibold text-gray-900">Maarif Soru Sihirbazı erişimi yok</div>
          <div className="mt-2 text-sm text-gray-600">Bu sayfayı görüntüleme yetkiniz bulunmuyor.</div>
        </div>
      </div>
    );
  }

  return <TabPage title="Maarif Soru Sihirbazı" tabs={tabs} />;
}
